import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  CalendarDays,
  Copy,
  Eye,
  MessageCircle,
  Newspaper,
  PenLine,
  Share2,
} from 'lucide-react';
import Card from '../components/common/Card';
import PageHero from '../components/common/PageHero';
import { toast } from 'react-toastify';
import api from '../services/api';
import { assetUrl } from '../utils/assets';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentForm, setCommentForm] = useState({
    name: '',
    email: '',
    comment: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);

  useEffect(() => {
    if (slug) {
      fetchPost();
    }
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await api.get(`/blog/posts/${slug}`);
      setPost(response.data);
      fetchComments(response.data.id);
    } catch (error) {
      console.error('Error fetching post:', error);
      navigate('/blog');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async (postId) => {
    try {
      const response = await api.get(`/blog/posts/${postId}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    
    if (!commentForm.name || !commentForm.email || !commentForm.comment) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post(`/blog/posts/${post.id}/comments`, commentForm);
      toast.success('Comment submitted for approval');
      setCommentForm({ name: '', email: '', comment: '' });
      fetchComments(post.id);
    } catch (error) {
      console.error('Error submitting comment:', error);
      toast.error('Failed to submit comment');
    } finally {
      setSubmitting(false);
    }
  };

  const getImageUrl = assetUrl;

  // Social Sharing Functions
  const shareOnFacebook = () => {
    const url = encodeURIComponent(window.location.href);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank', 'width=600,height=400');
  };

  const shareOnTwitter = () => {
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(post.title);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank', 'width=600,height=400');
  };

  const shareOnWhatsApp = () => {
    const url = window.location.href;
    const text = encodeURIComponent(`Check out this article: ${post.title}\n\n${url}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
      setShowShareMenu(false);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  if (loading) {
    return (
      <div className="bg-ink-50">
        <PageHero icon={Newspaper} title="Loading article..." />
        <div className="container-page py-16">
          <div className="flex items-center justify-center">
            <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-brand-700"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!post) return null;

  const shareButtonBase =
    'inline-flex h-10 w-10 items-center justify-center rounded-full shadow-sm transition-colors focus-visible:outline-none';

  return (
    <div className="bg-ink-50">
      <PageHero icon={Newspaper} title={post.title}>
        <div className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 text-sm text-ink-500 md:pl-14">
          {post.category_name && (
            <Link
              to={`/blog?category=${post.category_slug}`}
              className="badge-gold hover:underline"
            >
              {post.category_name}
            </Link>
          )}
          <span className="inline-flex items-center gap-1">
            <CalendarDays className="h-4 w-4" aria-hidden="true" />
            {new Date(post.published_at).toLocaleDateString()}
          </span>
          <span className="inline-flex items-center gap-1">
            <Eye className="h-4 w-4" aria-hidden="true" />
            {post.view_count || 0} views
          </span>
          <span className="inline-flex items-center gap-1">
            <PenLine className="h-4 w-4" aria-hidden="true" />
            by {post.author_name || 'Admin'}
          </span>
        </div>
      </PageHero>

      <div className="container-page py-8 sm:py-10">
        <div className="mx-auto max-w-3xl">
          {/* Back to the blog index */}
          <Link to="/blog" className="btn-ghost mb-5 -ml-2 text-sm">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Back to news
          </Link>

          {/* Hero image with the brand fallback layer */}
          {getImageUrl(post.featured_image) && (
            <figure className="mb-8 overflow-hidden rounded-2xl border border-ink-200/70 bg-brand-800 shadow-card">
              <div className="relative aspect-[16/9] w-full">
                <div className="absolute inset-0 flex items-center justify-center bg-brand-800">
                  <Newspaper className="h-10 w-10 text-white/40" aria-hidden="true" />
                </div>
                <img
                  src={getImageUrl(post.featured_image)}
                  alt={post.title}
                  className="relative h-full w-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            </figure>
          )}

          <article className="card p-6 sm:p-8 lg:p-10">
            <div
              className="blog-content max-w-none"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          </article>

          {/* Share row */}
          <div className="mt-8 flex flex-col gap-4 rounded-xl border border-ink-200/70 bg-white p-5 shadow-card sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2 text-sm font-semibold text-ink-700">
              <Share2 className="h-4 w-4 text-ink-400" aria-hidden="true" />
              Share this article
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={shareOnFacebook}
                aria-label="Share on Facebook"
                title="Share on Facebook"
                className={`${shareButtonBase} bg-brand-700 text-white hover:bg-brand-800`}
              >
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M18.77 7.46H14.5v-1.9c0-.9.6-1.1 1-1.1h3V.5h-4.33C10.24.5 9.5 3.44 9.5 5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4z" />
                </svg>
              </button>

              <button
                type="button"
                onClick={shareOnTwitter}
                aria-label="Share on X (formerly Twitter)"
                title="Share on X (formerly Twitter)"
                className={`${shareButtonBase} bg-ink-900 text-white hover:bg-ink-800`}
              >
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </button>

              <button
                type="button"
                onClick={shareOnWhatsApp}
                aria-label="Share on WhatsApp"
                title="Share on WhatsApp"
                className={`${shareButtonBase} bg-gold-500 text-brand-950 hover:bg-gold-600`}
              >
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12.03 20.75c-1.5 0-2.98-.4-4.27-1.17l-4.63 1.2 1.24-4.5c-.86-1.4-1.32-3.02-1.32-4.7 0-5.02 4.07-9.1 9.07-9.1 2.42 0 4.7.94 6.4 2.65 1.7 1.7 2.64 3.98 2.64 6.4 0 5-4.07 9.22-9.13 9.22zm0-16.7c-4.14 0-7.5 3.37-7.5 7.5 0 1.46.42 2.88 1.2 4.1l-.8 2.9 3-.8c1.2.7 2.6 1.1 4.1 1.1 4.14 0 7.5-3.37 7.5-7.5 0-4.14-3.36-7.3-7.5-7.3z" />
                  <path d="M15.8 14.6c-.2-.3-.8-.8-1.4-1.1-.6-.3-1-.3-1.4.1-.3.4-.8 1.1-1 1.3-.2.3-.4.4-.8.2-.8-.3-1.6-1-2.2-1.8-.4-.5-.6-.9-.7-1.2 0-.3.1-.5.2-.7.1-.2.2-.3.3-.5.1-.2.2-.3.2-.5 0-.2-.1-.4-.2-.6l-.5-1c-.2-.4-.4-.4-.6-.4h-.5c-.2 0-.5.1-.7.3-.2.2-.8.8-.8 2 0 1.2.9 2.4 1 2.6.1.2 1.7 2.6 4.1 3.6.6.2 1 .4 1.4.5.6.1 1.1.1 1.5 0 .5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.1-1.2z" />
                </svg>
              </button>

              <button
                type="button"
                onClick={copyLink}
                aria-label="Copy article link"
                title="Copy article link"
                className={`${shareButtonBase} bg-ink-100 text-ink-700 hover:bg-ink-200 hover:text-ink-900`}
              >
                <Copy className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>

          {/* Comments */}
          <section aria-labelledby="comments-heading" className="mt-12">
            <div className="mb-4 flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-ink-400" aria-hidden="true" />
              <h2 id="comments-heading" className="text-xl font-bold text-ink-900">
                Comments
              </h2>
              <span className="badge-neutral">{comments.length}</span>
            </div>

            <Card className="mb-6" hoverable={false}>
              <h3 className="text-base font-bold text-ink-900">Leave a comment</h3>
              <p className="mt-1 text-xs text-ink-500">
                Your comment will be visible after approval by the admin.
              </p>

              <form onSubmit={handleCommentSubmit} className="mt-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label htmlFor="comment-name" className="label">
                      Name
                    </label>
                    <input
                      id="comment-name"
                      type="text"
                      placeholder="Your name"
                      value={commentForm.name}
                      onChange={(e) => setCommentForm({...commentForm, name: e.target.value})}
                      className="input-field"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="comment-email" className="label">
                      Email
                    </label>
                    <input
                      id="comment-email"
                      type="email"
                      placeholder="you@example.com"
                      value={commentForm.email}
                      onChange={(e) => setCommentForm({...commentForm, email: e.target.value})}
                      className="input-field"
                      required
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label htmlFor="comment-body" className="label">
                    Comment
                  </label>
                  <textarea
                    id="comment-body"
                    rows="4"
                    placeholder="Share your thoughts"
                    value={commentForm.comment}
                    onChange={(e) => setCommentForm({...commentForm, comment: e.target.value})}
                    className="input-field"
                    required
                  />
                </div>

                <button type="submit" disabled={submitting} className="btn-primary mt-4">
                  {submitting ? 'Submitting...' : 'Submit comment'}
                </button>
              </form>
            </Card>

            {comments.length === 0 ? (
              <p className="rounded-xl border border-dashed border-ink-300 bg-white/60 px-6 py-8 text-center text-sm text-ink-500">
                No comments yet. Be the first to comment!
              </p>
            ) : (
              <ul className="space-y-4">
                {comments.map((comment) => (
                  <li key={comment.id}>
                    <Card hoverable={false}>
                      <div className="flex items-start gap-3">
                        <span
                          aria-hidden="true"
                          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-brand-100 text-sm font-bold uppercase text-brand-800"
                        >
                          {(comment.name || '?').charAt(0)}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                            <span className="text-sm font-semibold text-ink-900">
                              {comment.name}
                            </span>
                            <span className="text-xs text-ink-400">
                              {new Date(comment.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="mt-1 text-sm leading-relaxed text-ink-700">
                            {comment.comment}
                          </p>
                        </div>
                      </div>
                    </Card>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;
